// src/pages/VerifyEmail.js
import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { authAPI } from "../services/api";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setStatus("error");
        setErrorMessage("Invalid verification link. No token provided.");
        return;
      }

      try {
        const response = await authAPI.verifyEmail(token);
        
        // Store tokens for auto-login
        if (response.data.tokens) {
          localStorage.setItem("accessToken", response.data.tokens.accessToken);
          localStorage.setItem("refreshToken", response.data.tokens.refreshToken);
        }
        
        setStatus("success");
        
        // Redirect to dashboard after a moment
        setTimeout(() => {
          window.location.href = "/dashboard";
        }, 2000);
        
      } catch (err) {
        setStatus("error");
        setErrorMessage(
          err.response?.data?.error || 
          "Failed to verify email. The link may be expired or invalid."
        );
      }
    };

    verify();
  }, [token]);

  if (status === "verifying") {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: "100%", textAlign: "center" }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">Verifying your email...</Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (status === "error") {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: "100%", textAlign: "center" }}>
            <ErrorIcon sx={{ fontSize: 64, color: "error.main", mb: 2 }} />
            <Typography variant="h5" gutterBottom fontWeight={600}>
              Verification Failed
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: "left" }}>
              {errorMessage}
            </Alert>
            <Button
              variant="contained"
              onClick={() => navigate("/login")}
              sx={{ mr: 2 }}
            >
              Go to Login
            </Button>
            <Button
              variant="outlined"
              href="https://get.secondclear.com"
            >
              Sign Up Again
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: "100%", textAlign: "center" }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Email Verified!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your email has been verified successfully. Redirecting you to your dashboard...
          </Typography>
          <CircularProgress size={24} />
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyEmail;
